import React, { useEffect, useState } from 'react';
import { RotateCcw, Trash2, ArrowLeftRight, CheckCircle2, Info, X } from 'lucide-react';
import { undoToastManager, UndoToastData } from '../utils/undoToastManager';

export const UndoToast: React.FC = () => {
  const [toast, setToast] = useState<UndoToastData | null>(null);

  useEffect(() => {
    return undoToastManager.subscribe(currentToast => {
      setToast(currentToast);
    });
  }, []);

  if (!toast) return null;

  const handleUndo = async () => {
    if (toast.onUndo) {
      try {
        await toast.onUndo();
      } catch (err) {
        console.error('[UndoToast] Undo action failed:', err);
      }
    }
    undoToastManager.dismiss();
  };

  const getIcon = () => {
    switch (toast.icon) {
      case 'delete':
        return <Trash2 className="w-4 h-4 text-rose-400 shrink-0" />;
      case 'move':
        return <ArrowLeftRight className="w-4 h-4 text-indigo-400 shrink-0" />;
      case 'status':
        return <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />;
      default:
        return <Info className="w-4 h-4 text-amber-400 shrink-0" />;
    }
  };

  const isMac = typeof window !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

  return (
    <div
      role="status"
      data-testid="undo-toast"
      aria-live="polite"
      className="fixed bottom-6 left-6 z-[9999] flex items-center gap-3 px-4 py-3 bg-slate-900/95 dark:bg-slate-950/95 text-white border border-slate-700/80 shadow-2xl backdrop-blur-md rounded-xl max-w-xl animate-in fade-in slide-in-from-bottom-5 duration-200"
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="p-1.5 rounded-lg bg-slate-800/80 border border-slate-700/50">
          {getIcon()}
        </div>
        <p className="text-xs sm:text-sm font-medium text-slate-200 truncate pr-1">
          {toast.message}
        </p>
      </div>

      <div className="flex items-center gap-1.5 shrink-0 ml-auto pl-2 border-l border-slate-800">
        {toast.actions?.map(action => (
          <button
            key={action.label}
            type="button"
            onClick={async () => {
              try {
                await action.onClick();
              } catch (err) {
                console.error('[UndoToast] Action failed:', err);
              }
              undoToastManager.dismiss();
            }}
            className="px-2.5 py-1.5 text-xs font-semibold text-slate-200 hover:text-white bg-slate-800/80 hover:bg-slate-700 active:scale-95 border border-slate-700 rounded-lg transition-all whitespace-nowrap"
          >
            {action.label}
          </button>
        ))}
        {toast.onUndo && (
          <button
            type="button"
            onClick={handleUndo}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-indigo-300 hover:text-white bg-indigo-600/30 hover:bg-indigo-600/60 active:scale-95 border border-indigo-500/40 rounded-lg transition-all"
            title={`${toast.actionLabel || 'Rückgängig'} (${isMac ? 'Cmd+Z' : 'Strg+Z'})`}
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>{toast.actionLabel || 'Rückgängig'}</span>
            <kbd className="hidden sm:inline-block px-1 py-0.2 text-[9px] font-mono bg-slate-800 text-slate-400 border border-slate-700 rounded ml-1">
              {isMac ? '⌘Z' : 'Ctrl+Z'}
            </kbd>
          </button>
        )}

        <button
          type="button"
          onClick={() => undoToastManager.dismiss()}
          aria-label="Schließen"
          className="p-1 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
